/**
 * Geocoding utilities using Nominatim (OpenStreetMap)
 * Free geocoding service with no API key required
 */

const NOMINATIM_BASE_URL = 'https://nominatim.openstreetmap.org';

/**
 * Search for addresses using Nominatim
 * @param {string} query - Search query
 * @returns {Promise<Array>} Array of address results
 */
export const searchAddress = async (query) => {
  if (!query || query.length < 3) {
    return [];
  }

  try {
    const response = await fetch(
      `${NOMINATIM_BASE_URL}/search?format=json&q=${encodeURIComponent(query)}&limit=5&countrycodes=it&addressdetails=1`,
      {
        headers: {
          'User-Agent': 'PropertySimulator/1.0'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Geocoding error: ${response.status}`);
    }

    const results = await response.json();
    
    // Filter and format results
    return results
      .filter(result => result.lat && result.lon && result.display_name)
      .map(result => ({
        place_id: result.place_id,
        display_name: result.display_name,
        lat: result.lat,
        lon: result.lon,
        type: result.type,
        importance: result.importance
      }))
      .sort((a, b) => (b.importance || 0) - (a.importance || 0)); // Sort by importance
      
  } catch (error) {
    console.error('Address search error:', error);
    return [];
  }
};

/**
 * Reverse geocode coordinates to get address
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {Promise<string|null>} Address string or null
 */
export const reverseGeocode = async (lat, lng) => {
  try {
    const response = await fetch(
      `${NOMINATIM_BASE_URL}/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`,
      {
        headers: {
          'User-Agent': 'PropertySimulator/1.0'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Reverse geocoding error: ${response.status}`);
    }

    const result = await response.json();
    
    if (result && result.display_name) {
      return result.display_name;
    }

    return null;
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    return null;
  }
};

/**
 * Get place details by place ID
 * @param {string} placeId - Nominatim place ID
 * @returns {Promise<Object|null>} Place details or null
 */
export const getPlaceDetails = async (placeId) => {
  try {
    const response = await fetch(
      `${NOMINATIM_BASE_URL}/details?format=json&place_id=${placeId}&addressdetails=1`,
      {
        headers: {
          'User-Agent': 'PropertySimulator/1.0'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Place details error: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Place details error:', error);
    return null;
  }
};

/**
 * Validate coordinates
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {boolean} True if coordinates are valid
 */
export const validateCoordinates = (lat, lng) => {
  return (
    typeof lat === 'number' && 
    typeof lng === 'number' &&
    lat >= -90 && lat <= 90 &&
    lng >= -180 && lng <= 180
  );
};

/**
 * Check if coordinates are within Italy bounds (approximate)
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {boolean} True if coordinates are in Italy
 */
export const isInItaly = (lat, lng) => {
  // Approximate bounding box for Italy
  const ITALY_BOUNDS = {
    north: 47.1,
    south: 35.5,
    east: 18.5,
    west: 6.6
  };

  return (
    lat >= ITALY_BOUNDS.south &&
    lat <= ITALY_BOUNDS.north &&
    lng >= ITALY_BOUNDS.west &&
    lng <= ITALY_BOUNDS.east
  );
};